import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {connect} from 'react-redux';
import {Route, Redirect, Switch, withRouter} from 'react-router-dom';
import {isEmpty} from 'lodash/lang';
import FlashMessage from 'components/FlashMessage';
import Routes from './routes';
import {SideBar, NavBar} from './Navigation/PrimaryNavigation';
import UserAgreement from './UserAgreement';
import Login from './Account/Login';
import ForgotPassword from './Account/ForgotPassword';
import {AcceptInvitation, AcceptSubscription, ResetForgotPassword} from 'components/Accept';
import {switchActiveSchool, switchActiveClassroom} from 'api/account';
import {fetchCurrentAccount, setCurrentAccount, logout, removeAuthInfo} from 'actions/account';
import {formatErrors} from 'actions/errors';
import withoutAuth from 'utils/without-auth';
import withAuth from 'utils/with-auth';
import {levelMap, requestNotificationPermission} from 'utils/common';
import * as permissions from 'utils/permissions';

class App extends React.Component {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      isAuthenticated: null,
    };
    this.handleLogout = this.handleLogout.bind(this);
    this.switchActiveSchool = this.switchActiveSchool.bind(this);
    this.switchActiveClassroom = this.switchActiveClassroom.bind(this);
    this.renderWithoutAuth = this.renderWithoutAuth.bind(this);
    this.renderWithAuth = this.renderWithAuth.bind(this);
  }

  componentDidMount() {
    requestNotificationPermission();
    if (!localStorage.getItem('kluwellJwt')) {
      this.setState({isAuthenticated: false});
    } else {
      this.props
        .fetchCurrentAccount()
        .then(() => this.setState({isAuthenticated: true}))
        .catch(() => {
          removeAuthInfo();
          this.setState({isAuthenticated: false});
        });
    }
  }

  handleLogout() {
    const {logout, formatErrors} = this.props;
    logout()
      .then(() => {
        this.context.router.history.push('/');
      })
      .catch((err) => formatErrors(err));
  }

  switchActiveSchool(schoolId) {
    const {setCurrentAccount, formatErrors} = this.props;
    return switchActiveSchool(schoolId)
      .then(({record}) => setCurrentAccount(record))
      .catch((err) => formatErrors(err));
  }

  switchActiveClassroom(classroomId) {
    const {user, setCurrentAccount, formatErrors} = this.props;
    if (user.toJS().activeClassroom.id === classroomId) {
      return Promise.resolve();
    }
    return switchActiveClassroom(classroomId)
      .then(({record}) => setCurrentAccount(record))
      .catch((err) => formatErrors(err));
  }

  renderWithoutAuth() {
    return (
      <Switch>
        <Route path="/trial" render={(props) => React.createElement(withoutAuth(Login), {...props, isTrial: true})} />
        <Route path="/accept/forgot-password" component={withoutAuth(ResetForgotPassword)} />
        <Route path="/account/forgot-password" component={withoutAuth(ForgotPassword)} />
        <Route path="/account/login" component={withoutAuth(Login)} />
        <Redirect to="/account/login" />
      </Switch>
    );
  }

  renderWithAuth(user, role, accountSocket) {
    const userAgreementRead = user.get('userAgreementRead');
    if (!permissions.isKluwellAdmin(role) && !permissions.isReader(role) && !userAgreementRead) {
      return (
        <Switch>
          <Route exact path="/account/user-agreement" component={UserAgreement} />
          <Redirect to="/account/user-agreement" />
        </Switch>
      );
    }
    user = user.toJS();
    return (
      <React.Fragment>
        <NavBar
          user={user}
          accountSocket={accountSocket}
          switchActiveSchool={this.switchActiveSchool}
          switchActiveClassroom={this.switchActiveClassroom}
          handleLogout={this.handleLogout}
        />
        <SideBar user={user} accountSocket={accountSocket} handleLogout={this.handleLogout} />
        <div key="routes" className="main-content">
          {React.createElement(withAuth(Routes), {role})}
        </div>
      </React.Fragment>
    );
  }

  render() {
    if (this.state.isAuthenticated === null) {
      return null;
    }
    const {user, accountSocket} = this.props;
    const role = user.get('role');
    const userLevel = user.get('level') ? levelMap[user.get('level', 1)].toLowerCase() : null;
    return (
      <div
        className={classNames({
          'stretch-height': true,
          reader: role === 'reader',
          [`${userLevel}-level`]: !isEmpty(userLevel),
        })}
      >
        <FlashMessage />
        <Switch>
          <Route exact path="/accept/invitation" component={AcceptInvitation} />
          <Route exact path="/accept/subscription" component={AcceptSubscription} />
          <Route
            path="*"
            render={() => (role ? this.renderWithAuth(user, role, accountSocket) : this.renderWithoutAuth())}
          />
        </Switch>
      </div>
    );
  }
}

export default withRouter(
  connect(
    ({account}) => ({
      user: account.get('user'),
      accountSocket: account.get('accountSocket'),
    }),
    {
      formatErrors,
      fetchCurrentAccount,
      setCurrentAccount,
      logout,
    },
  )(App),
);
